// src/layout/Layout.jsx
// 認証済みページ用の枠。Sidebar + Topbar + メインコンテンツ。
// 【2026-07-21】スマホ対応: サイドバーをドロワー化し、下部タブバーを追加。
//   - drawerOpen: スマホ幅でのドロワー開閉状態（デスクトップでは常時表示でこの状態は無視される）
// 【2026-10-13】全画面で出すページ（出席記録）を足した。
//   サイドバーとトップバーを隠し、画面の左端にカーソルを寄せるとサイドバーが上に重なって出る。
//   ページ側の「メニュー」ボタンからも開ける（useShell().openNav）。マウスの無い端末はボタンだけ。
//     ・左端に 0.15 秒とどまったら開く（通りすがりで開かないように）
//     ・サイドバーから外れたら 0.3 秒後に閉じる（斜めに動かしたときに消えないように）
//     ・ボタンで開いたときは、外側を押すか Esc で閉じる

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import MobileTabBar from './MobileTabBar';
import AndroidAppBanner from '../components/AndroidAppBanner';

const ShellContext = createContext({ bare: false, openNav: () => {} });
/** 全画面のページから、サイドバーを開くのに使う */
export const useShell = () => useContext(ShellContext);

// 全画面で出すページ。/classes/:id と /classes/:id/summary（と古い export / import）は出席記録
const BARE_RE = /^\/classes\/[^/]+(\/(summary|export|import))?\/?$/;

export default function Layout({ user, onLogout, children }) {
  const location = useLocation();
  const bare = BARE_RE.test(location.pathname);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  // 全画面のときのサイドバー： null（閉じている）/ 'hover'（左端で開いた）/ 'click'（ボタンで開いた）
  const [peek, setPeek] = useState(null);
  const timer = useRef(null);
  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => () => clearTimer(), []);
  useEffect(() => { clearTimer(); setPeek(null); setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!peek) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPeek(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [peek]);

  const openNav = useCallback(() => { clearTimer(); setPeek(p => (p ? null : 'click')); }, []);
  const ctx = useMemo(() => ({ bare, openNav }), [bare, openNav]);

  if (bare) {
    return (
      <ShellContext.Provider value={ctx}>
        <div className="app-shell-bare">
          {/* 左端の見えない帯。ここにカーソルを寄せるとサイドバーが出る（マウスの無い端末では出さない：CSS） */}
          <div className="edge-peek" aria-hidden="true"
               onMouseEnter={() => { clearTimer(); if (!peek) timer.current = setTimeout(() => setPeek('hover'), 150); }}
               onMouseLeave={() => { if (!peek) clearTimer(); }} />
          <Sidebar user={user} open={!!peek} peek
                   onClose={() => { clearTimer(); setPeek(null); }}
                   onMouseEnter={clearTimer}
                   onMouseLeave={() => {
                     if (peek !== 'hover') return;
                     clearTimer();
                     timer.current = setTimeout(() => setPeek(null), 300);
                   }} />
          {peek === 'click' && <div className="peek-backdrop" onClick={() => setPeek(null)} />}
          <main className="bare-main">{children}</main>
        </div>
      </ShellContext.Provider>
    );
  }

  return (
    <ShellContext.Provider value={ctx}>
      <div className="app-shell">
        <Sidebar user={user} open={drawerOpen} onClose={closeDrawer} />
        {/* ドロワーの外側タップで閉じる背景（スマホのみ表示） */}
        {drawerOpen && <div className="drawer-backdrop" onClick={closeDrawer} />}
        <div className="app-main">
          <Topbar user={user} onLogout={onLogout} onMenuClick={() => setDrawerOpen(o => !o)} />
          <main className="main-content">
            <AndroidAppBanner />
            {children}
          </main>
        </div>
        <MobileTabBar user={user} onMenuClick={() => setDrawerOpen(true)} />
      </div>
    </ShellContext.Provider>
  );
}
